import { __awaiter, __generator, __spreadArray } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import SendOutlined from "@mui/icons-material/SendOutlined";
import { Chip, Tooltip } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { SocialShareTileGrid, parseEmailsFromText, isValidEmail, } from "@corporate-trip-planner-fe/ui-components";
import { useClipboard } from "@corporate-trip-planner-fe/hooks";
import { setModal } from "../../../../store/slices";
import { getTripDuration } from "../../../../utils/helpers/functions";
import TripPrivacyDetails from "../../../common/trip-privacy/TripPrivacyDetails";
import { TripAccessCode } from "../../../../utils/enums/TripAccessCode";
import { CloseBtn, CopyLinkBtn, DividerRow, EmailChipWrap, EmailHint, EmailInput, ErrorText, HeroSubtitle, HeroTitle, LinkField, LinkRow, ModalRoot, PrivacyBox, SectionLabel, SendBtn, SharingLabel, SocialSection, TopBar, TripMeta, TripMetaText, TripThumb, TripTitleLine, } from "./invite-friends.style";
var formatDisplayUrl = function (href) {
    try {
        var u = new URL(href);
        return "".concat(u.host).concat(u.pathname);
    }
    catch (_a) {
        return href;
    }
};
var formatDurationLabel = function (startDate, endDate) {
    var duration = getTripDuration(startDate, endDate).duration;
    if (duration <= 1)
        return "".concat(duration, "D");
    return "".concat(duration - 1, "N/").concat(duration, "D");
};
function InviteFriendsModal(_a) {
    var _this = this;
    var trip = _a.trip, tripId = _a.tripId, tripName = _a.tripName, onSubmit = _a.onSubmit;
    var dispatch = useDispatch();
    var shareUrl = typeof window !== "undefined" ? window.location.href : "";
    var displayUrl = formatDisplayUrl(shareUrl);
    var _b = useClipboard(), copied = _b.copied, copyToClipboard = _b.copyToClipboard;
    var _c = useState(""), inputValue = _c[0], setInputValue = _c[1];
    var _d = useState([]), emails = _d[0], setEmails = _d[1];
    var _e = useState({}), emailStatuses = _e[0], setEmailStatuses = _e[1];
    var _f = useState(false), actionLoading = _f[0], setActionLoading = _f[1];
    var _g = useState(null), inputError = _g[0], setInputError = _g[1];
    var privacy = TripPrivacyDetails[trip.privacySetting] ||
        TripPrivacyDetails[TripAccessCode.INVITE];
    var durationLabel = formatDurationLabel(trip.startDate, trip.endDate);
    var titleLine = "".concat(tripName, " \u00B7 ").concat(durationLabel);
    var handleClose = function () { return dispatch(setModal(null)); };
    var addEmails = useCallback(function (raw) {
        var parsed = parseEmailsFromText(raw);
        if (!parsed.length && raw.trim() && !isValidEmail(raw.trim())) {
            setInputError("Enter a valid email address");
            return;
        }
        setInputError(null);
        if (!parsed.length)
            return;
        setEmails(function (prev) { return Array.from(new Set(__spreadArray(__spreadArray([], prev, true), parsed, true))); });
    }, []);
    var commitInput = function () {
        if (!inputValue.trim())
            return;
        addEmails(inputValue);
        setInputValue("");
    };
    var handleKeyDown = function (e) {
        if (e.key === "Enter" || e.key === "," || e.key === ";") {
            e.preventDefault();
            commitInput();
        }
        else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
            setEmails(function (prev) { return prev.slice(0, -1); });
        }
    };
    var handlePaste = function (e) {
        e.preventDefault();
        addEmails(e.clipboardData.getData("text"));
        setInputValue("");
    };
    var collectAllEmails = function () {
        var pending = parseEmailsFromText(inputValue);
        return Array.from(new Set(__spreadArray(__spreadArray([], emails, true), pending, true)));
    };
    var handleSubmit = function () { return __awaiter(_this, void 0, void 0, function () {
        var allEmails, response, _a, successful, failed, newStatuses_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    allEmails = collectAllEmails();
                    if (inputValue.trim()) {
                        setEmails(allEmails);
                        setInputValue("");
                    }
                    if (allEmails.length === 0) {
                        setInputError("Add at least one email address");
                        return [2 /*return*/];
                    }
                    setActionLoading(true);
                    return [4 /*yield*/, onSubmit(tripId, { invitedEmails: allEmails })];
                case 1:
                    response = _b.sent();
                    setActionLoading(false);
                    if (response === null || response === void 0 ? void 0 : response.result) {
                        _a = response.result, successful = _a.successful, failed = _a.failed;
                        newStatuses_1 = {};
                        successful === null || successful === void 0 ? void 0 : successful.forEach(function (email) {
                            newStatuses_1[email] = { status: "success" };
                        });
                        failed === null || failed === void 0 ? void 0 : failed.forEach(function (failObj) {
                            newStatuses_1[failObj.email] = {
                                status: "failed",
                                reason: failObj.reason,
                            };
                        });
                        setEmailStatuses(newStatuses_1);
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(ModalRoot, { children: [_jsxs(TopBar, { children: [_jsxs(TripMeta, { children: [_jsx(TripThumb, { "$image": trip.coverImageUrl }), _jsxs(TripMetaText, { children: [_jsx(SharingLabel, { children: "Sharing" }), _jsx(TripTitleLine, { children: titleLine })] })] }), _jsx(CloseBtn, { type: "button", onClick: handleClose, "aria-label": "Close", children: "\u00D7" })] }), _jsx(HeroTitle, { children: "Invite Friends" }), _jsx(HeroSubtitle, { children: "Copy the link or send invites directly by email." }), _jsx(SectionLabel, { children: "Trip link" }), _jsxs(LinkRow, { children: [_jsxs(LinkField, { children: [_jsx(LinkIcon, { sx: { fontSize: 18, color: "#7a7368", flexShrink: 0 } }), _jsx("span", { title: shareUrl, children: displayUrl })] }), _jsxs(CopyLinkBtn, { type: "button", "$copied": copied, onClick: function () { return copyToClipboard(shareUrl, "Link copied to clipboard"); }, children: [copied ? (_jsx(CheckIcon, { sx: { fontSize: 16 } })) : (_jsx(ContentCopyIcon, { sx: { fontSize: 16 } })), copied ? "Copied" : "Copy Link"] })] }), _jsxs(SocialSection, { children: [_jsx(SectionLabel, { children: "Share via" }), _jsx(SocialShareTileGrid, { url: shareUrl, title: tripName })] }), _jsx(DividerRow, { children: _jsx("span", { children: "Or invite by email" }) }), _jsx(SectionLabel, { children: "Email addresses" }), emails.length > 0 && (_jsx(EmailChipWrap, { children: emails.map(function (email) {
                    var statusInfo = emailStatuses[email];
                    return (_jsx(Chip, { size: "small", label: _jsxs("span", { style: {
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                            }, children: [email, (statusInfo === null || statusInfo === void 0 ? void 0 : statusInfo.status) === "failed" && (_jsx(Tooltip, { title: statusInfo.reason || "Failed", children: _jsx(InfoOutlined, { sx: { fontSize: 14 } }) }))] }), onDelete: function () {
                            return setEmails(function (prev) { return prev.filter(function (e) { return e !== email; }); });
                        }, color: (statusInfo === null || statusInfo === void 0 ? void 0 : statusInfo.status) === "success" ? "success" : undefined }, email));
                }) })), _jsx(EmailInput, { type: "text", placeholder: "name@example.com", value: inputValue, onChange: function (e) {
                    setInputValue(e.target.value);
                    setInputError(null);
                }, onKeyDown: handleKeyDown, onBlur: commitInput, onPaste: handlePaste }), _jsx(EmailHint, { children: "Press Enter or comma to add multiple addresses" }), inputError && _jsx(ErrorText, { children: inputError }), _jsxs(SendBtn, { type: "button", onClick: handleSubmit, disabled: actionLoading, children: [_jsx(SendOutlined, { sx: { fontSize: 18 } }), "Send Invitations"] }), _jsxs(PrivacyBox, { "$bg": privacy.backgroundColor, "$border": privacy.borderColor, "$color": privacy.color, children: [_jsx("span", { style: { flexShrink: 0, display: "flex", marginTop: 2 }, children: privacy.iconComponent }), _jsxs("span", { children: [_jsx("strong", { children: privacy.label }), " ", privacy.description] })] })] }));
}
export default InviteFriendsModal;
